import { createGraphNode, type NodeLinks } from './graphNode'
import type {
  GraphDatapointEdge,
  GraphDexpiEdge,
  GraphEdge,
  GraphNode,
  PnidProgressEntry,
} from './graphTypes'
import type { PnidEntry } from './pnidSchemas'

export class PnidGraph {
  pageId: string
  progressEntry: PnidProgressEntry | undefined
  isIncomplete: boolean
  #nodes = new Map<string, GraphNode>()
  #nodeList: GraphNode[]
  #dexpiEdges = new Map<string, GraphDexpiEdge>()
  #datapointEdges = new Map<string, GraphDatapointEdge>()

  static fromEntries(pageId: string, entries: PnidEntry[]): PnidGraph {
    return new PnidGraph(pageId, entries)
  }

  private constructor(pageId: string, input: PnidEntry[]) {
    this.pageId = pageId
    const entries = [
      ...new Map(input.map((entry) => [entry.id, entry])).values(),
    ].map((entry) => structuredClone(entry))
    const links = new Map<string, NodeLinks>()
    for (const entry of entries) {
      if (entry.type === 'pnid-node') {
        const nodeLinks: NodeLinks = {
          outgoing: [],
          incoming: [],
          overlays: [],
        }
        links.set(entry.id, nodeLinks)
        this.#nodes.set(entry.id, createGraphNode(entry, nodeLinks))
      }
    }
    for (const entry of entries) {
      if (entry.type !== 'pnid-edge') continue
      const { data } = entry
      const sourceNode = this.#nodes.get(data.source_node_id)
      const sourceLinks = links.get(data.source_node_id)
      if (!sourceNode || !sourceLinks) continue
      if (data.type === 'datapoint_association') {
        const edge: GraphDatapointEdge = {
          kind: 'datapoint_association',
          id: entry.id,
          tags: entry.tags,
          data,
          sourceNode,
          targetDatapointId: data.target_node_id,
        }
        this.#datapointEdges.set(edge.id, edge)
        sourceLinks.outgoing.push(edge)
        continue
      }
      const targetNode = this.#nodes.get(data.target_node_id)
      const targetLinks = links.get(data.target_node_id)
      if (!targetNode || !targetLinks) continue
      const edge: GraphDexpiEdge = {
        kind: 'dexpi',
        id: entry.id,
        tags: entry.tags,
        data,
        sourceNode,
        targetNode,
      }
      this.#dexpiEdges.set(edge.id, edge)
      sourceLinks.outgoing.push(edge)
      targetLinks.incoming.push(edge)
    }
    for (const entry of entries) {
      if (entry.type !== 'pnid-node-overlay' || entry.data.page_id !== pageId)
        continue
      links.get(entry.data.node_id)?.overlays.push(entry)
    }
    const progress = entries.filter(
      (entry): entry is PnidProgressEntry =>
        entry.type === 'pnid-extraction-progress' &&
        entry.data.page_id === pageId
    )
    this.progressEntry = progress.length === 1 ? progress[0] : undefined
    this.isIncomplete =
      entries.length > 0 && this.progressEntry?.data.status !== 'complete'
    this.#nodeList = [...this.#nodes.values()]
  }

  getNode(nodeId: string): GraphNode | undefined {
    return this.#nodes.get(nodeId)
  }
  getEdge(edgeId: string): GraphEdge | undefined {
    return this.#dexpiEdges.get(edgeId) ?? this.#datapointEdges.get(edgeId)
  }
  getNodes(): GraphNode[] {
    return this.#nodeList
  }
  getEdges(): GraphEdge[] {
    return [...this.#dexpiEdges.values(), ...this.#datapointEdges.values()]
  }
  getDexpiEdges(): GraphDexpiEdge[] {
    return [...this.#dexpiEdges.values()]
  }
  getDatapointEdges(): GraphDatapointEdge[] {
    return [...this.#datapointEdges.values()]
  }
}
